// Shared recurrence-occurrence math for the onboarding form (the details
// controller computes the "Ends on" date + hidden end_date; the attendance-days
// preview renders the day list). Mirrors the SERVER generation exactly:
//   - weekly: IceCube::Rule.weekly.day(*chips).count(N)
//   - monthly: IceCube::Rule.monthly.<rule>.count(N)
// so the form preview matches the sub-events that will actually be created.

export const WEEKDAY_INDEX = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};

// "YYYY-MM-DD" → local Date (avoid the UTC shift of new Date(str)).
export function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = String(str).split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Date → "YYYY-MM-DD" (local).
export function isoDate(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

// The first N weekly occurrences across the selected weekday chips, starting at
// `start` (the start date itself counts when its weekday is selected). Matches
// IceCube weekly.day(*days).count(N). `days` is an array of lowercase weekday
// names. Returns up to N Date objects in chronological order.
export function weeklyFirstN(start, days, n) {
  const wanted = new Set(days.map((d) => WEEKDAY_INDEX[String(d).toLowerCase()]).filter((x) => x != null));
  if (wanted.size === 0 || n <= 0) return [];
  const out = [];
  const d = new Date(start);
  // Cap the day-walk so a bad input can't loop forever (N weeks * 7 + slack).
  for (let i = 0; out.length < n && i < n * 7 + 14; i++) {
    if (wanted.has(d.getDay())) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

// The first N monthly occurrences for a monthly rule string
// (day_of_month(N) | day_of_week(:wd => [n]) | day_of_week(:wd => [-1])),
// starting from `start`'s month. day_of_month clamps to each month's last day;
// day_of_week matches IceCube monthly.<rule>.count(N).
export function monthlyFirstN(start, rule, n) {
  if (!rule || n <= 0) return [];
  const out = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);

  // Digit groups bounded to 1-2 chars to match the server allowlist
  // (MonthlyRecurrenceRule) and reject-fast on out-of-range values.
  const dom = rule.match(/day_of_month\((\d{1,2})\)/);
  const dow = rule.match(/day_of_week\(:(\w+)\s*=>\s*\[(-?\d{1,2})\]\)/);

  // Cap month-walk so a date that never occurs (e.g. day_of_month(31)) can't
  // loop forever; N*2 months of slack is plenty for the supported rules.
  for (let i = 0; out.length < n && i < n * 2 + 24; i++) {
    let occ = null;
    if (dom) {
      const day = parseInt(dom[1], 10);
      const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
      if (day <= last) occ = new Date(cursor.getFullYear(), cursor.getMonth(), day);
    } else if (dow) {
      const weekday = WEEKDAY_INDEX[dow[1].toLowerCase()];
      const nth = parseInt(dow[2], 10);
      occ = nth === -1
        ? lastWeekdayOfMonth(cursor.getFullYear(), cursor.getMonth(), weekday)
        : nthWeekdayOfMonth(cursor.getFullYear(), cursor.getMonth(), weekday, nth);
    }
    if (occ && occ >= start) out.push(occ);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return out;
}

export function nthWeekdayOfMonth(year, month, weekday, nth) {
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  const day = 1 + offset + (nth - 1) * 7;
  const last = new Date(year, month + 1, 0).getDate();
  return day <= last ? new Date(year, month, day) : null;
}

export function lastWeekdayOfMonth(year, month, weekday) {
  const last = new Date(year, month + 1, 0);
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - offset);
}
